import React from "react";
import Reveal from "./Reveal";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "../../lib/track";

/**
 * Work With Me.
 *
 * The guided staircase. Six rungs presented in ascending order of
 * commitment so the visitor reads it as a progression, not a menu:
 *
 *   1. Clarity Snapshot (free)        - route to /frequency-snapshot
 *   2. The Book ($19.99)              - route to /book
 *   3. Personal Action Plan ($49.99)  - fires am:open-blueprint-checkout
 *   4. Clarity Reset Session ($149)   - fires am:open-orientation-reset-checkout
 *   5. Structured Momentum Sprint     - consultation modal (surface: sprint)
 *   6. Strategic Architecture Container - consultation modal (surface: architecture)
 *
 * Stripe product names upstream are unchanged ("Personal Action Plan",
 * "Clarity Reset Session") so receipts, conversions, and attribution
 * stay continuous. Tiers 5 and 6 do NOT advertise prices; they route
 * to the existing consultation modal so a conversation begins before
 * money changes hands.
 */

const ENTRY_RUNGS = [
  {
    id: "clarity-snapshot",
    name: "Clarity Snapshot",
    price: "Free",
    what: "A free reflective diagnostic that helps you see where your momentum is strong, where it is blocked, and what kind of next step may make sense.",
    bestFor: "Best for people who want to understand where they are before deciding anything. No email required. You see your result first.",
    cta: { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  },
  {
    id: "book",
    name: "The Book",
    price: "$19.95",
    what: "The philosophy and foundation. The full Authentic Momentum framework, in your own hands, at your own pace.",
    bestFor: "Best for people who want to sit with the ideas before applying them.",
    cta: { label: "Explore the Book", href: "/book" },
  },
];

const TIERS = [
  {
    id: "clarity-session",
    label: "Clarity Reset Session",
    sub: "A paid guided session",
    price: "$149",
    purpose:
      "A paid guided session for people who want help interpreting what keeps circling, choosing the next practical move, and turning insight into structure.",
    body:
      "One focused conversation to name what is happening, see the pattern, and leave with a grounded, practical next step.",
    when:
      "Choose this when you are circling a decision, transition, opportunity, or pattern and want help turning it into a practical next move.",
    cta: {
      label: "Book the Clarity Reset Session · $149",
      kind: "orientation-reset",
    },
  },
  {
    id: "momentum-sprint",
    label: "Structured Momentum Sprint",
    sub: "Deeper guided implementation",
    price: "By application",
    purpose:
      "For people who need a deeper map for their brand, offer, idea, business, content, AI system, or execution pathway.",
    body:
      "A strategic sprint designed to turn scattered ideas into a clear structure, actionable roadmap, and practical next moves.",
    when:
      "Choose this when you are ready to turn clarity into a short, focused execution arc.",
    cta: {
      label: "Apply for a Sprint",
      kind: "consultation",
      surface: "sprint",
    },
  },
  {
    id: "architecture-container",
    label: "Strategic Architecture Container",
    sub: "High-level strategic support",
    price: "By invitation",
    purpose:
      "For founders, operators, and builders who need deeper advisory, systems design, launch strategy, AI-assisted infrastructure, or long-term ecosystem buildout.",
    body:
      "A deeper strategic partnership for people or ventures ready to turn complex potential into a functioning system, offer, brand, or operating pathway.",
    when:
      "Choose this when one decision is not the problem, and you need structure across multiple moving parts.",
    cta: {
      label: "Request Strategic Support",
      kind: "consultation",
      surface: "architecture",
    },
  },
];

const fireCta = (tier) => {
  trackEvent("work_with_me_cta_clicked", { tier: tier.id });
  if (tier.cta.kind === "orientation-reset") {
    window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
    return;
  }
  if (tier.cta.kind === "blueprint") {
    window.dispatchEvent(new CustomEvent("am:open-blueprint-checkout"));
    return;
  }
  // consultation - reuses the existing Path consultation modal channel
  window.dispatchEvent(
    new CustomEvent("am:open-consultation", {
      detail: { surface: tier.cta.surface, tier: tier.id, label: tier.label },
    })
  );
};

// Slim entry rung. Visually lighter than the three main tier cards so
// visitors read these as the warm-up steps of the staircase.
const EntryRung = ({ rung, children }) => (
  <article
    data-testid={`work-entry-${rung.id}`}
    className="bg-white/[0.035] border-t border-gold/15 p-5 md:p-6 rounded-lg border border-gold/10 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
  >
    <div className="md:flex-1">
      <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
        <h3 className="font-serif text-lg md:text-xl text-themed font-light">
          {rung.name}
        </h3>
        <span className="text-[12px] tracking-[0.18em] uppercase text-gold/85">
          {rung.price}
        </span>
      </div>
      <p className="mt-2 text-white/80 text-[14.5px] md:text-[15px] leading-relaxed">
        {rung.what}
      </p>
      <p className="mt-1.5 text-white/65 text-[13.5px] md:text-[14px] leading-relaxed">
        {rung.bestFor}
      </p>
    </div>
    {children}
  </article>
);

const WorkWithMe = () => (
  <section
    id="work-with-me"
    data-testid="work-with-me-section"
    className="relative py-24 md:py-32 border-t border-gold/10"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
          Work With Me
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center max-w-3xl mx-auto">
          Choose the step that matches{" "}
          <span className="italic gold-gradient-text">your season.</span>
        </h2>
        <p
          data-testid="work-with-me-intro"
          className="mt-7 max-w-2xl mx-auto text-center text-white/85 leading-relaxed"
        >
          Different people enter at different levels. You do not need to choose the biggest step first. Start with the level that matches how much clarity and support you need right now.
        </p>
      </Reveal>

      <div data-testid="work-with-me-ladder" className="mt-12 space-y-4 md:space-y-5">
        {/* Rung 1 + 2: free Snapshot and the Book. Quiet entry rungs. */}
        {ENTRY_RUNGS.map((rung, i) => (
          <Reveal key={rung.id} delay={60 + i * 60}>
            <EntryRung rung={rung}>
              <a
                href={rung.cta.href}
                data-testid={`work-entry-cta-${rung.id}`}
                onClick={() => trackEvent("work_with_me_cta_clicked", { tier: rung.id })}
                className="btn-ghost-gold inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto"
              >
                {rung.cta.label} <ArrowRight size={13} />
              </a>
            </EntryRung>
          </Reveal>
        ))}

        {/* Rung 3: Personal Action Plan ($49.99). Same Stripe product as
            before - the upstream name stays "Personal Action Plan" so
            receipts and attribution are continuous. */}
        <Reveal delay={180}>
          <article
            data-testid="work-tier-personal-action-plan"
            className="bg-white/[0.035] border-t border-gold/15 p-5 md:p-6 rounded-lg border border-gold/10 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
          >
            <div className="md:flex-1">
              <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                <h3 className="font-serif text-lg md:text-xl text-themed font-light">
                  Personal Action Plan
                </h3>
                <span className="text-[12px] tracking-[0.18em] uppercase text-gold/85">$49.99</span>
              </div>
              <p className="mt-2 text-white/80 text-[14.5px] md:text-[15px] leading-relaxed">
                A practical written plan. A privately synthesized next-move sequence built from your own words.
              </p>
              <p className="mt-1.5 text-white/65 text-[13.5px] md:text-[14px] leading-relaxed">
                Best for people who want a clearer next move without booking a live session.
              </p>
            </div>
            <button
              type="button"
              data-testid="work-tier-cta-personal-action-plan"
              onClick={() =>
                fireCta({
                  id: "personal-action-plan",
                  label: "Personal Action Plan",
                  cta: { kind: "blueprint" },
                })
              }
              className="btn-ghost-gold inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto"
            >
              Start the Personal Action Plan <ArrowRight size={13} />
            </button>
          </article>
        </Reveal>

        {/* Rungs 4 to 6: the guided tiers. */}
        {TIERS.map((t, i) => (
          <Reveal key={t.id} delay={220 + i * 80}>
            <article
              data-testid={`work-tier-${t.id}`}
              className="bg-white/5 border-t border-gold/20 p-6 md:p-8 rounded-lg border border-gold/10"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-5 lg:gap-8">
                <div className="lg:w-2/3">
                  <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                    <h3 className="font-serif text-xl md:text-2xl text-themed font-light leading-snug">
                      {t.label}
                    </h3>
                    <span className="text-[11px] tracking-[0.22em] uppercase text-gold/85">
                      {t.price}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] tracking-[0.18em] uppercase text-gold/70">
                    {t.sub}
                  </p>
                  <p className="mt-4 text-white/85 text-[15px] md:text-[15.5px] leading-relaxed">
                    {t.purpose}
                  </p>
                  <p className="mt-3 text-white/70 text-[14.5px] md:text-[15px] leading-relaxed">
                    {t.body}
                  </p>
                  <p
                    data-testid={`work-tier-when-${t.id}`}
                    className="mt-3 text-white/65 text-[13.5px] md:text-[14px] leading-relaxed italic"
                  >
                    {t.when}
                  </p>
                </div>
                <div className="lg:w-1/3 lg:flex lg:justify-end lg:items-start">
                  <button
                    type="button"
                    data-testid={`work-tier-cta-${t.id}`}
                    onClick={() => fireCta(t)}
                    className="btn-gold inline-flex items-center gap-2 whitespace-nowrap"
                  >
                    {t.cta.label} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={400}>
        <p className="mt-12 text-center text-[12px] tracking-[0.22em] uppercase text-gold/70">
          Not sure where to start?{" "}
          <a
            href="/frequency-snapshot"
            data-testid="work-with-me-snapshot-hint"
            className="text-gold hover:text-[#F3E5AB] transition-colors underline underline-offset-4 decoration-gold/40"
          >
            Take the free Clarity Snapshot first.
          </a>
        </p>
      </Reveal>
    </div>
  </section>
);

export default WorkWithMe;
